// Lazy, throttled page thumbnails rendered with PDF.js.
import { sharedPdf, thumbnail } from '../lib/engines/pdfjs';
import { h } from './dom';
import { icon } from './icons';

const ids = new WeakMap<Uint8Array, number>();
let nextId = 1;
const keyOf = (bytes: Uint8Array, page: number) => {
  let id = ids.get(bytes);
  if (!id) {
    id = nextId++;
    ids.set(bytes, id);
  }
  return `${id}:${page}`;
};

export class ThumbnailLoader {
  private cache = new Map<string, Promise<string>>();
  private queue: Array<() => Promise<void>> = [];
  private active = 0;
  private observer: IntersectionObserver;
  private pending = new WeakMap<Element, () => void>();

  constructor(private readonly concurrency = 2) {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.observer.unobserve(entry.target);
          this.pending.get(entry.target)?.();
          this.pending.delete(entry.target);
        }
      },
      { rootMargin: '400px' },
    );
  }

  /** Thumbnail URL for a page, rendered once and shared. */
  get(bytes: Uint8Array, page: number, maxSide = 220): Promise<string> {
    const key = keyOf(bytes, page);
    let promise = this.cache.get(key);
    if (!promise) {
      promise = new Promise<string>((resolve, reject) => {
        this.queue.push(async () => {
          try {
            const doc = await sharedPdf(bytes);
            resolve((await thumbnail(doc, page, maxSide)).url);
          } catch (err) {
            reject(err);
          }
        });
        this.pump();
      });
      this.cache.set(key, promise);
    }
    return promise;
  }

  private pump() {
    while (this.active < this.concurrency && this.queue.length) {
      const job = this.queue.shift()!;
      this.active++;
      job().finally(() => {
        this.active--;
        this.pump();
      });
    }
  }

  /** Fills `container` with the thumbnail once it scrolls into view. */
  attach(container: HTMLElement, bytes: Uint8Array, page: number, alt: string, rotation = 0): void {
    container.replaceChildren(h('div', { class: 'spinner', 'aria-hidden': 'true' }));
    const load = () => {
      this.get(bytes, page).then(
        (url) => {
          const img = h('img', { src: url, alt, decoding: 'async', draggable: false });
          if (rotation) img.style.transform = `rotate(${rotation}deg)`;
          container.replaceChildren(img);
        },
        () => container.replaceChildren(icon('file')),
      );
    };
    this.pending.set(container, load);
    this.observer.observe(container);
  }

  dispose(): void {
    this.observer.disconnect();
    this.queue = [];
    for (const p of this.cache.values()) p.then((url) => URL.revokeObjectURL(url), () => undefined);
    this.cache.clear();
  }
}
